import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { connect } from 'react-redux';
import { Drawer, Table, Space, Button, Modal, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import * as RevSettleApi from '@/api/bill/revSettle';
import { BizErrorCode } from '@/api/bizCode';
import { parseFen2Currency } from '@/utils/number';

import { initSettledBillList } from '@/redux/actions/bill/revSettle';

import styles from './index.module.scss';
import commonStyles from '../common.module.scss';

import { OpenRevSettleDrawerProps, RevSettleDrawerProps } from '../interface';

const RevSettle: React.FC<RevSettleDrawerProps> = forwardRef((props, ref) => {
  const { settledBillListLoading, settledBillList } = props;

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedSettlementNo, setSelectedSettlementNo] = useState('');
  const paramsRef = useRef<OpenRevSettleDrawerProps>();

  const openDrawer = (params: OpenRevSettleDrawerProps) => {
    paramsRef.current = params;
    setDrawerVisible(true);
    props.initSettledBillList({ resvNo: params.resvNo });
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  const visibleChange = (visible) => {
    if (!visible) {
      setSelectedSettlementNo('');
    }
  };

  const revSettleConfirm = async () => {
    setSubmitLoading(true);
    try {
      const billItem = settledBillList.find((item) => item.settlementNo === selectedSettlementNo);
      const res = await RevSettleApi.submitRevSettle({
        settlementNo: selectedSettlementNo,
        resvNo: paramsRef.current?.resvNo,
        accountNumber: billItem.settlementQuantity,
      });
      if (res && res.status) {
        message.success(intl.get('bill_rev_settle_success'));
        setDrawerVisible(false);
        // callback parent component
        props.revSettleCallback();
      } else {
        message.error(res.msg || intl.get('bill_rev_settle_fail'));
      }
      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
      if (
        err.code === BizErrorCode.REV_SETTLE_ERROR ||
        err.code === BizErrorCode.REV_SETTLE_CRS_ERROR
      ) {
        Modal.error({
          title: intl.get('bill_rev_settle_fail'),
          content: err?.msg,
          closable: true,
          okText: intl.get('app_confirm_confirm_btn'),
        });
      }
    }
  };

  // 反结账提交
  const submit = async () => {
    Modal.confirm({
      title: intl.get('bill_rev_settle_title'),
      content: intl.get('bill_rev_settle_modal_content'),
      okButtonProps: {
        loading: submitLoading,
      },
      closable: true,
      onOk: async () => {
        await revSettleConfirm();
      },
    });
  };

  const rowSelection = {
    selectedRowKeys: [selectedSettlementNo],
    onChange: (rowKeys) => {
      setSelectedSettlementNo(rowKeys[0]);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.recoverFlag,
    }),
  };

  const columns: ColumnsType<any> = [
    {
      title: intl.get('bill_rev_settle_settle_no'),
      dataIndex: 'settlementNo',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_rev_settle_settle_time'),
      dataIndex: 'settlementTime',
      render: (val) => getTimeByUtcOffset(val, DATE_TIME_FORMAT),
    },
    {
      title: intl.get('bill_rev_settle_settle_quantity'),
      dataIndex: 'settlementQuantity',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_rev_settle_settle_amount'),
      dataIndex: 'settlementAmount',
      align: 'right',
      render: (val) => parseFen2Currency(val),
    },
  ];

  const drawerFooter = () => {
    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        <Button type="primary" disabled={!selectedSettlementNo} onClick={() => submit()}>
          {intl.get('bill_rev_settle_title')}
        </Button>
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  return (
    <Drawer
      width="50%"
      open={drawerVisible}
      title={intl.get('bill_rev_settle_title')}
      maskClosable={false}
      closable={false}
      afterOpenChange={(v) => visibleChange(v)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <dl className={styles['revSettle-table-wrapper']}>
        <dt className={commonStyles['bill-block-title']}>
          {intl.get('bill_rev_settle_choose_settle_bill')}
        </dt>
        <dd>{intl.get('bill_rev_settle_only_support_last_night_settle')}</dd>
        <dd>
          <Table
            className={styles['revSettle-table']}
            rowKey="settlementNo"
            loading={settledBillListLoading}
            dataSource={settledBillList}
            columns={columns}
            rowSelection={{
              type: 'radio',
              ...rowSelection,
            }}
            pagination={false}
          />
        </dd>
      </dl>
    </Drawer>
  );
});

const mapStateToProps = (state) => {
  const { settledBillListLoading, settledBillList } = state.revSettle;

  return { settledBillListLoading, settledBillList };
};

export default connect(mapStateToProps, { initSettledBillList }, null, { forwardRef: true })(
  RevSettle,
);
